import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useLang } from "@/lib/i18n";

const HERO_IMG =
  "https://images.unsplash.com/photo-1663530761401-15eefb544889?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxmaW5lJTIwZGluaW5nJTIwZm9vZCUyMGRhcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc5MDgwNTkyMnww&ixlib=rb-4.1.0&q=85";

const scrollTo = (href) => {
  if (window.__lenis) {
    window.__lenis.scrollTo(href, { offset: -72, duration: 1.4 });
  } else {
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  }
};

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="hjem"
      ref={ref}
      className="relative min-h-screen flex items-end overflow-hidden"
      data-testid="hero-section"
    >
      <motion.div style={{ y: imgY }} className="absolute inset-0 scale-110">
        <img
          src={HERO_IMG}
          alt={t.hero.imgAlt}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#110E0C] via-[#110E0C]/60 to-[#110E0C]/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#110E0C]/90 via-[#110E0C]/40 to-transparent" />
      </motion.div>

      <motion.div
        style={{ y: contentY, opacity: fade }}
        className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32 pt-48 w-full"
      >
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D48C46] mb-8"
          data-testid="hero-eyebrow"
        >
          {t.hero.eyebrow}
        </motion.p>

        <h1
          className="font-serif font-medium text-5xl sm:text-6xl lg:text-8xl tracking-tight leading-[1.12] text-[#F9F6F0] [text-shadow:0_2px_40px_rgba(17,14,12,0.9),0_1px_6px_rgba(17,14,12,0.7)]"
          data-testid="hero-heading"
        >
          {t.hero.lines.map((line, i) => (
            <span className="mask-line" key={line.text}>
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{
                  duration: 1.1,
                  delay: 0.25 + i * 0.16,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className={line.accent ? "italic text-[#D48C46]" : ""}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-[#F9F6F0]/70"
          data-testid="hero-subheading"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => scrollTo("#kontakt")}
            data-testid="hero-cta-button"
            className="inline-flex items-center rounded-sm bg-[#D48C46] px-8 py-4 text-sm font-semibold text-[#110E0C] hover:bg-[#F0A862] transition-colors duration-300"
          >
            {t.hero.ctaPrimary}
          </button>
          <button
            onClick={() => scrollTo("#slik-fungerer-det")}
            data-testid="hero-secondary-button"
            className="inline-flex items-center gap-2 rounded-sm border border-[#F9F6F0]/25 px-8 py-4 text-sm font-semibold text-[#F9F6F0] hover:border-[#D48C46] hover:text-[#D48C46] transition-colors duration-300"
          >
            {t.hero.ctaSecondary}
            <ArrowDown size={16} />
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
}
